import React, { useState, useMemo } from "react";
import BlogCard from "./BlogCard";
import websiteData from "../../data/websiteData.json";

interface BlogListProps {
  // Define any props if this component were to accept them
}

const BlogList: React.FC<BlogListProps> = () => {
  const { blogs } = websiteData;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("newest");

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ["All", ...new Set(blogs.posts.map((post) => post.category))];
    return cats;
  }, [blogs.posts]);

  // Filter and sort posts
  const filteredPosts = useMemo(() => {
    let filtered = blogs.posts.filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesCategory =
        selectedCategory === "All" || post.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    // Sort posts
    filtered.sort((a, b) => {
      if (sortBy === "newest") {
        return (
          new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
        );
      } else if (sortBy === "oldest") {
        return (
          new Date(a.publishDate).getTime() - new Date(b.publishDate).getTime()
        );
      } else if (sortBy === "featured") {
        return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      }
      return 0;
    });

    return filtered;
  }, [blogs.posts, searchTerm, selectedCategory, sortBy]);

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold mb-3 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 bg-clip-text text-transparent day-theme:from-purple-600 day-theme:via-pink-600 day-theme:to-red-600">
            {blogs.title}
          </h1>
          <p className="text-base lg:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed day-theme:text-slate-700">
            {blogs.subtitle}
          </p>
          <div className="w-12 h-1 bg-gradient-to-r from-purple-400 to-pink-400 mx-auto mt-4 rounded-full"></div>
        </div>

        {/* Filters and Search */}
        <div className="mb-8">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 day-theme:bg-white/20 day-theme:border-slate-300/50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-300 mb-1.5 day-theme:text-slate-700">
                  Search Posts
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by title, content, or tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 day-theme:bg-white/20 day-theme:border-slate-300 day-theme:text-slate-900 day-theme:placeholder-slate-500"
                  />
                  <svg
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5 day-theme:text-slate-700">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 day-theme:bg-white/20 day-theme:border-slate-300 day-theme:text-slate-900"
                >
                  {categories.map((category) => (
                    <option
                      key={category}
                      value={category}
                      className="bg-gray-800 text-white"
                    >
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5 day-theme:text-slate-700">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 day-theme:bg-white/20 day-theme:border-slate-300 day-theme:text-slate-900"
                >
                  <option value="newest" className="bg-gray-800 text-white">
                    Newest First
                  </option>
                  <option value="oldest" className="bg-gray-800 text-white">
                    Oldest First
                  </option>
                  <option value="featured" className="bg-gray-800 text-white">
                    Featured First
                  </option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-3 pt-3 border-t border-white/10 day-theme:border-slate-300/30">
              <p className="text-xs text-gray-400 day-theme:text-slate-600">
                Showing {filteredPosts.length} of {blogs.posts.length} posts
                {searchTerm && ` for "${searchTerm}"`}
                {selectedCategory !== "All" && ` in ${selectedCategory}`}
              </p>
            </div>
          </div>
        </div>

        {/* Blog Posts Grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 max-w-2xl mx-auto day-theme:bg-white/20 day-theme:border-slate-300/50">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 day-theme:text-slate-900">
                No posts found
              </h3>
              <p className="text-gray-300 mb-6 day-theme:text-slate-700">
                Try adjusting your search terms or filters to find what you're
                looking for.
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCategory("All");
                  setSortBy("newest");
                }}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-300 day-theme:from-[#DA4453] day-theme:to-[#89216B] day-theme:hover:from-[#C13A47] day-theme:hover:to-[#7A1D5F]"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
